import React, { useEffect, useState } from 'react'
import { Route, Redirect } from 'react-router-dom'
import { signInRedirect } from '../../services/auth.service'
import roleConstants from '../../constants/roles.constants'
import tokenConstants from '../../constants/token.constants'
import PropTypes from 'prop-types'
import { useLocation } from 'react-router'
import { serviceAccessCheck } from '../../actions/auth.actions'
import { useSelector, useDispatch } from 'react-redux'
import LoadingPage from '../loadingPage'
import authLogger from '../../utils/authLogger'

// Outside component — persists across React StrictMode remounts
let hasRedirected = false

const ProtectedRoute = ({ requiredRoles, component: Component, ...rest }) => {
  const location = useLocation()
  const dispatch = useDispatch()

  const { user, serviceHasAccess, isCheckingServiceAccess } = useSelector(
    state => state.auth
  )

  const [isLoading, setIsLoading] = useState(true)
  const [hasRoles, setHasRoles] = useState(false)
  const [redirectFailed, setRedirectFailed] = useState(false)

  // Log on mount
  useEffect(() => {

    authLogger.info('ProtectedRoute', 'Component mounted', {
      path: location.pathname,
      hasUser: !!user,
      userExpired: user?.expired,
      hasRedirectedFlag: hasRedirected,
    })
    authLogger.logUserState('ProtectedRoute', user)

    return () => {
      authLogger.debug('ProtectedRoute', 'Component unmounting', { path: location.pathname })
    }
  }, [])

  useEffect(() => {
    const userIsValid = user && !user.expired
    let redirectTimeout = null

    authLogger.info('ProtectedRoute', 'User effect triggered', {
      userIsValid,
      hasUser: !!user,
      userExpired: user?.expired,
      hasRedirectedFlag: hasRedirected,
      path: location.pathname,
    })

    if (userIsValid && hasRedirected) {
      authLogger.info('ProtectedRoute', 'User is valid, resetting redirect flag')
      hasRedirected = false
      setRedirectFailed(false)
    }

    if (!userIsValid && !hasRedirected) {
      hasRedirected = true
      const postLoginRedirect = window.location.pathname + window.location.search

      authLogger.warn('ProtectedRoute', 'User invalid - initiating redirect to login', {
        postLoginRedirect,
        userExpired: user?.expired,
        hasUser: !!user,
      })

      // Set a timeout in case redirect doesn't work (common on iOS)
      redirectTimeout = setTimeout(() => {
        authLogger.error('ProtectedRoute', 'Redirect timeout reached - showing manual login option', {
          timeoutMs: 10000,
          path: location.pathname,
        })
        setRedirectFailed(true)
      }, 10000) // 10 second timeout

      signInRedirect({ state: { postLoginRedirect } })
        .then(() => {
          authLogger.info('ProtectedRoute', 'signInRedirect promise resolved')
          clearTimeout(redirectTimeout)
        })
        .catch(err => {
          authLogger.error('ProtectedRoute', 'signInRedirect failed', { error: err.message })
          clearTimeout(redirectTimeout)
          hasRedirected = false
          setRedirectFailed(true)
        })
    }

    // Cleanup timeout on unmount or when user changes
    return () => {
      if (redirectTimeout) {
        authLogger.debug('ProtectedRoute', 'Cleaning up redirect timeout')
        clearTimeout(redirectTimeout)
      }
    }
  }, [user])

  useEffect(() => {
    async function initialiseAccessData() {
      if (!user) {
        setIsLoading(false)
        return
      }

      if (serviceHasAccess === null) {
        dispatch(serviceAccessCheck())
      }

      let userHasRoles = false
      const rolesRequired = requiredRoles && requiredRoles.length
      if (rolesRequired) {
        if (
          typeof user.profile[tokenConstants.ROLES] === 'string' &&
          requiredRoles.length === 1
        ) {
          userHasRoles = user.profile[tokenConstants.ROLES] === requiredRoles[0]
        } else if (Array.isArray(user.profile[tokenConstants.ROLES])) {
          userHasRoles = requiredRoles.every(role =>
            user.profile[tokenConstants.ROLES].includes(role)
          )
        }
      }

      setHasRoles(rolesRequired ? userHasRoles : true)
      setIsLoading(false)
    }

    initialiseAccessData()
  }, [dispatch, requiredRoles, serviceHasAccess, user])

  if (!user || user.expired) {
    if (redirectFailed) {
      authLogger.warn('ProtectedRoute', 'Rendering manual login fallback UI', {
        path: location.pathname,
        hasUser: !!user,
        userExpired: user?.expired,
      })
      return (
        <LoadingPage
          text='Unable to redirect automatically. Please click below to login.'
          action={{
            text: 'Login',
            onClick: () => {
              authLogger.info('ProtectedRoute', 'Manual login button clicked')
              hasRedirected = false
              setRedirectFailed(false)
              const postLoginRedirect = window.location.pathname + window.location.search
              signInRedirect({ state: { postLoginRedirect } })
            }
          }}
        />
      )
    }
    authLogger.info('ProtectedRoute', 'Rendering redirect to login UI')
    return <LoadingPage text='Redirecting to login...' />
  }

  if (isLoading || isCheckingServiceAccess) {
    authLogger.debug('ProtectedRoute', 'Checking permissions', { isLoading, isCheckingServiceAccess })
    return <LoadingPage text='Checking permissions...' />
  }

  if (!serviceHasAccess) {
    authLogger.warn('ProtectedRoute', 'No service access - redirecting to /access')
    return <Redirect to='/access' />
  }

  if (!hasRoles) {
    authLogger.warn('ProtectedRoute', 'Missing required roles - redirecting to /permissions', { requiredRoles })
    return (
      <Redirect
        to={{
          pathname: '/permissions',
          state: { from: location.pathname },
        }}
      />
    )
  }

  authLogger.debug('ProtectedRoute', 'Rendering protected component', { path: location.pathname })
  return <Route {...rest} render={props => <Component {...props} />} />
}

ProtectedRoute.propTypes = {
  requiredRoles: PropTypes.arrayOf(
    PropTypes.oneOf([
      roleConstants.ADMIN,
      roleConstants.BUSINESS,
      roleConstants.HR,
      roleConstants.KNOWLEDGE_PORTAL,
      roleConstants.MI,
    ])
  ),
  component: PropTypes.func.isRequired,
}

export default ProtectedRoute
